import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { useApp } from "@/context/AppContext";
import { globalSearch } from "@/services/services";
import { Briefcase, Users, FileText, Gavel, PlusCircle, Receipt, PenLine, GitBranch, Loader2, BookOpen, ListChecks, StickyNote } from "lucide-react";

const COMMANDS = [
  { label: "Create new matter", to: "/matters?new=1", icon: PlusCircle },
  { label: "Create client", to: "/clients?new=1", icon: Users },
  { label: "Open billing", to: "/billing", icon: Receipt },
  { label: "Draft writ petition", to: "/drafting", icon: PenLine },
  { label: "Tomorrow hearings", to: "/calendar", icon: Gavel },
];

const ICONS = { Matter: Briefcase, Client: Users, Document: FileText, Hearing: Gavel, Draft: PenLine, Research: FileText, Authority: BookOpen, Task: ListChecks, Note: StickyNote };

export function CommandPalette({ open, onOpenChange }) {
  const { db, search } = useApp();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [term, setTerm] = useState("");
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(false);
  const local = globalSearch(db, term);
  const matterMatch = pathname.match(/^\/matters\/([^/]+)$/);
  const matterCmds = matterMatch ? [
    { label: "Open ProofGraph", to: `/matters/${matterMatch[1]}?tab=ProofGraph`, icon: GitBranch },
    { label: "Show matter gaps (ProofGraph)", to: `/matters/${matterMatch[1]}?tab=ProofGraph&view=gaps`, icon: GitBranch },
  ] : [];

  useEffect(() => { if (!open) { setTerm(""); setServer(null); } }, [open]);

  // PERF-02: authorised server-side search (debounced); the hydrated-workspace match is only an instant fallback.
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setServer(null); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => search(q).then((r) => alive && setServer(r)).catch(() => alive && setServer(null)).finally(() => alive && setLoading(false)), 220);
    return () => { alive = false; clearTimeout(t); };
  }, [term]); // eslint-disable-line

  const results = server ?? local;
  const go = (to) => { onOpenChange(false); navigate(to); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search matters, clients, documents, authorities…" value={term} onValueChange={setTerm} data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>{loading ? "Searching…" : "No results found."}</CommandEmpty>
        {results.length > 0 && (
          <CommandGroup heading={<span className="flex items-center gap-2">Results {loading && <Loader2 className="h-3 w-3 animate-spin" />}{server && <span className="text-[10px] font-normal text-ink-muted">server search</span>}</span>}>
            {results.map((r) => {
              const Icon = ICONS[r.type] || FileText;
              return (
                <CommandItem key={`${r.type}-${r.id}`} value={`${r.label} ${r.sub || ""} ${r.id}`} onSelect={() => r.to && go(r.to)} data-testid={`search-result-${r.type.toLowerCase()}-${r.id}`}>
                  <Icon className="mr-2 h-4 w-4 text-copper" />
                  <span className="flex-1 truncate">{r.label}</span>
                  {r.sub && <span className="mr-2 truncate text-xs text-ink-2">{r.sub}</span>}
                  <span className="text-xs text-ink-muted">{r.type}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        <CommandSeparator />
        <CommandGroup heading="Commands">
          {[...matterCmds, ...COMMANDS].map((c) => (
            <CommandItem key={c.label} value={c.label} onSelect={() => go(c.to)}>
              <c.icon className="mr-2 h-4 w-4 text-ink-2" />
              {c.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
